import type { ReactNode } from "react";

import type { IReviewProviderScoreData } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

export interface IReviewProviderBadgeProps {
  data: IReviewProviderScoreData;
  className?: string;
  variant?: "default" | "compact";
  showReviewCount?: boolean;
  /**
   * Platform name ("Trustpilot", "Google") exposed as screen-reader/crawler
   * text when no visible `providerLabel` is rendered. The logo is then hidden
   * from assistive tech so the name is announced exactly once.
   */
  providerName?: string;
  /** Visible platform name rendered after the logo. */
  providerLabel?: string;
  reviewCountText?: string;
  gradeLabel?: string;
  logo?: ReactNode;
  starsOrGrade?: ReactNode;
}

export const ReviewProviderBadge = ({
  data,
  className,
  variant = "default",
  showReviewCount = false,
  providerName,
  providerLabel,
  reviewCountText,
  gradeLabel,
  logo,
  starsOrGrade,
}: IReviewProviderBadgeProps) => {
  const scoreText = `${data.score} / ${data.maxScore}`;
  // The name is carried by text (visible label or sr-only providerName), so
  // the logo — and any <title>/aria-label it brings — must stay silent.
  const isLogoDecorative = Boolean(providerLabel || providerName);

  return (
    <a
      href={data.linkToProviderReviews}
      target="_blank"
      rel="noreferrer"
      className={cn("flex items-center gap-1.5 overflow-visible", className)}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      {providerName && !providerLabel && (
        // Trailing space: inline text would otherwise run into the next word
        // ("TrustpilotUtmärkt") in the accessible name and in crawled text.
        <span className="sr-only">{`${providerName} `}</span>
      )}
      {starsOrGrade}

      {variant !== "compact" && (
        <span className="shrink-0 text-nowrap text-base-strong">
          {gradeLabel ? `${gradeLabel} ${scoreText}` : scoreText}
        </span>
      )}
      {variant === "compact" && gradeLabel && (
        <span className="shrink-0 text-nowrap text-base-strong">
          {gradeLabel}
        </span>
      )}
      {variant === "compact" && (
        <span className="sr-only">{` ${scoreText} `}</span>
      )}
      {logo != null ? (
        <span
          aria-hidden={isLogoDecorative || undefined}
          className="shrink-0 self-start inline-flex h-6 items-center overflow-visible"
        >
          {logo}
        </span>
      ) : null}
      {providerLabel && (
        <span className="shrink-0 text-nowrap text-base-strong">
          {providerLabel}
        </span>
      )}
      {showReviewCount && reviewCountText && (
        <>
          <span className="text-base-strong" style={{ opacity: 0.6 }}>
            |
          </span>
          <span
            className="shrink-0 text-nowrap text-base-strong"
            style={{ opacity: 0.6 }}
          >
            {reviewCountText}
          </span>
        </>
      )}
    </a>
  );
};
